<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">Amonestaciones</span>
          <v-spacer></v-spacer>
        </v-card-title>

        <!-- Tabs para organizar información -->
        <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
          <v-tab>
            <v-icon left>mdi-list-box</v-icon>
            Catálogo de Amonestaciones
          </v-tab>
          <v-tab>
            <v-badge color="red" :content="warningsHistory.length.toString()" overlap>
              <v-icon left>mdi-history</v-icon>
              Historial
            </v-badge>
          </v-tab>
          <v-tab>
            <v-icon left>mdi-shield-account</v-icon>
            Expediente de Jugadores
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="transparent">
          <!-- Tab 1: Catálogo de Amonestaciones -->
          <v-tab-item>
            <v-card class="elevation-0">
              <v-card-text class="pb-0">
                <p class="subtitle-2 grey--text mb-4">
                  Estas son las amonestaciones predefinidas que puedes registrar.
                  Cada una tiene un valor de strikes asociado.
                </p>
              </v-card-text>
              <v-data-table
                :headers="catalogHeaders"
                :items="warningCatalog"
                class="elevation-0"
                :no-data-text="$t('no_records_yet')">

                <template v-slot:item.name="{ item }">
                  <div class="font-weight-bold">{{ item.name }}</div>
                </template>

                <template v-slot:item.category="{ item }">
                  <v-chip small :color="getCategoryColor(item.category)" text-color="white">
                    {{ getCategoryLabel(item.category) }}
                  </v-chip>
                </template>

                <template v-slot:item.severity="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-alert-circle</v-icon>
                    <v-chip :color="getSeverityColor(item.severity)" text-color="white" small>
                      {{ item.severity }} strike{{ item.severity > 1 ? 's' : '' }}
                    </v-chip>
                  </div>
                </template>

                <template v-slot:item.description="{ item }">
                  <div class="caption grey--text">{{ item.description }}</div>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- Tab 2: Historial de Amonestaciones -->
          <v-tab-item>
            <v-card class="elevation-0">
              <v-card-subtitle class="pa-4">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <v-icon small class="mr-2">mdi-information</v-icon>
                    Total de amonestaciones registradas: {{ warningsHistory.length }}
                  </div>
                  <v-text-field
                    v-model="warningSearch"
                    prepend-inner-icon="mdi-magnify"
                    label="Buscar por jugador..."
                    outlined
                    dense
                    style="max-width: 300px;"
                  ></v-text-field>
                </div>
              </v-card-subtitle>
              <v-data-table
                :headers="warningHeaders"
                :items="filteredWarnings"
                class="elevation-0"
                :no-data-text="$t('no_records_yet')">

                <template v-slot:item.date="{ item }">
                  <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                  <div class="caption grey--text">{{ item.time }}</div>
                </template>

                <template v-slot:item.player="{ item }">
                  <div class="font-weight-bold">{{ item.player }}</div>
                  <div class="caption grey--text">Grupo {{ item.group }}</div>
                </template>

                <template v-slot:item.warning_type="{ item }">
                  <v-chip small :color="getCategoryColor(item.category)">
                    {{ item.warning_type }}
                  </v-chip>
                </template>

                <template v-slot:item.strikes="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-alert-circle</v-icon>
                    <strong>{{ item.strikes }}</strong>
                  </div>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" @click="viewWarningDetails(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver detalles</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- Tab 3: Expediente de Jugadores -->
          <v-tab-item>
            <v-card class="elevation-0">
              <v-card-subtitle class="pa-4">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <v-icon small class="mr-2">mdi-information</v-icon>
                    Jugadores con amonestaciones registradas
                  </div>
                  <v-text-field
                    v-model="playerSearch"
                    prepend-inner-icon="mdi-magnify"
                    label="Buscar jugador..."
                    outlined
                    dense
                    style="max-width: 300px;"
                  ></v-text-field>
                </div>
              </v-card-subtitle>
              <v-data-table
                :headers="playerHeaders"
                :items="filteredPlayers"
                class="elevation-0"
                :no-data-text="$t('no_records_yet')">

                <template v-slot:item.player_name="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="white--text mr-2">
                      <span class="caption">{{ item.initials }}</span>
                    </v-avatar>
                    <div>
                      <div class="font-weight-bold">{{ item.player_name }}</div>
                      <div class="caption grey--text">ID: {{ item.player_id }}</div>
                    </div>
                  </div>
                </template>

                <template v-slot:item.total_warnings="{ item }">
                  <v-chip small color="blue lighten-2">
                    {{ item.total_warnings }}
                  </v-chip>
                </template>

                <template v-slot:item.strikes="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-1" :color="getStrikesColor(item.strikes)">
                      mdi-alert-circle
                    </v-icon>
                    <strong :class="`${getStrikesColor(item.strikes)}--text`">
                      {{ item.strikes }}/3
                    </strong>
                  </div>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip v-if="item.restricted" small color="red" text-color="white">
                    <v-icon small left>mdi-lock</v-icon>
                    Bloqueado
                  </v-chip>
                  <v-chip v-else small color="green" text-color="white">
                    Activo
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" @click="viewPlayerFile(item)">
                        <v-icon small>mdi-file-document</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver expediente completo</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-flex>
    </v-layout>

    <!-- Diálogo de detalles de amonestación -->
    <WarningDetailDialog
      :dialog.sync="warningDetailDialog"
      :warning="selectedWarning"
    />

    <!-- Diálogo de expediente del jugador -->
    <PlayerFileDialog
      :dialog.sync="playerFileDialog"
      :player="selectedPlayer"
      :warnings="selectedPlayerWarnings"
    />
  </v-container>
</template>

<script>
import WarningDetailDialog from './components/WarningDetailDialog.vue'
import PlayerFileDialog from './components/PlayerFileDialog.vue'

export default {
  name: 'MarshallWarnings',
  components: {
    WarningDetailDialog,
    PlayerFileDialog
  },
  data () {
    return {
      activeTab: 0,
      warningSearch: '',
      playerSearch: '',
      warningDetailDialog: false,
      playerFileDialog: false,
      selectedWarning: null,
      selectedPlayer: null,
      selectedPlayerWarnings: [],
      catalogHeaders: [
        { text: 'Amonestación', value: 'name', width: '20%' },
        { text: 'Categoría', value: 'category', width: '15%' },
        { text: 'Strikes', value: 'severity', width: '12%' },
        { text: 'Descripción', value: 'description', width: '53%', sortable: false }
      ],
      warningHeaders: [
        { text: 'Fecha/Hora', value: 'date', width: '15%' },
        { text: 'Jugador', value: 'player', width: '25%' },
        { text: 'Amonestación', value: 'warning_type', width: '20%' },
        { text: 'Strikes', value: 'strikes', width: '10%' },
        { text: 'Acciones', value: 'actions', width: '10%', sortable: false, align: 'center' }
      ],
      playerHeaders: [
        { text: 'Jugador', value: 'player_name', width: '30%' },
        { text: 'Total Amonestaciones', value: 'total_warnings', width: '15%' },
        { text: 'Strikes Acumulados', value: 'strikes', width: '15%' },
        { text: 'Estado', value: 'status', width: '20%' },
        { text: 'Acciones', value: 'actions', width: '10%', sortable: false, align: 'center' }
      ],
      // MOCK DATA - Catálogo de amonestaciones
      warningCatalog: [
        {
          code: 'SLOW_PACE',
          name: 'Ritmo Lento',
          description: 'El grupo avanza más lentamente de lo permitido',
          category: 'pace',
          severity: 1
        },
        {
          code: 'DAMAGE',
          name: 'Daño al Campo',
          description: 'Daño causado al campo de juego',
          category: 'damage',
          severity: 1
        },
        {
          code: 'ATTIRE',
          name: 'Violación de Código de Vestimenta',
          description: 'Incumplimiento de las reglas de vestimenta del club',
          category: 'attire',
          severity: 1
        },
        {
          code: 'CONDUCT',
          name: 'Conducta Inapropiada',
          description: 'Comportamiento que viola las reglas de cortesía del golf',
          category: 'conduct',
          severity: 2
        },
        {
          code: 'RULES_VIOLATION',
          name: 'Violación de Reglas de Juego',
          description: 'Incumplimiento de las reglas oficiales del golf',
          category: 'rules',
          severity: 1
        },
        {
          code: 'VERBAL_WARNING',
          name: 'Amonestación Verbal',
          description: 'Advertencia sin aplicación de strikes',
          category: 'warning',
          severity: 0
        }
      ],
      // MOCK DATA - Historial de amonestaciones
      warningsHistory: [
        {
          id: 1,
          player: 'Fernando Ruiz',
          player_id: 101,
          group: 2,
          warning_type: 'Ritmo Lento',
          category: 'pace',
          date: '2025-08-04',
          time: '08:15',
          strikes: 1,
          notes: 'Grupo muy lento en hoyo 4'
        },
        {
          id: 2,
          player: 'Miguel Sánchez',
          player_id: 102,
          group: 2,
          warning_type: 'Ritmo Lento',
          category: 'pace',
          date: '2025-08-04',
          time: '08:45',
          strikes: 1,
          notes: 'Segunda amonestación por ritmo lento'
        },
        {
          id: 3,
          player: 'Javier Torres',
          player_id: 103,
          group: 4,
          warning_type: 'Daño al Campo',
          category: 'damage',
          date: '2025-08-03',
          time: '09:20',
          strikes: 1,
          notes: 'Daño en green del hoyo 8'
        },
        {
          id: 4,
          player: 'Enrique Vega',
          player_id: 104,
          group: 4,
          warning_type: 'Violación de Código de Vestimenta',
          category: 'attire',
          date: '2025-08-02',
          time: '07:00',
          strikes: 1,
          notes: 'Pantalones no conformes a código'
        },
        {
          id: 5,
          player: 'Raúl Soto',
          player_id: 105,
          group: 5,
          warning_type: 'Conducta Inapropiada',
          category: 'conduct',
          date: '2025-08-01',
          time: '10:30',
          strikes: 2,
          notes: 'Comportamiento inapropiado con otros jugadores'
        },
        {
          id: 6,
          player: 'Fernando Ruiz',
          player_id: 101,
          group: 2,
          warning_type: 'Amonestación Verbal',
          category: 'warning',
          date: '2025-07-31',
          time: '07:30',
          strikes: 0,
          notes: 'Advertencia verbal por ruido excesivo'
        }
      ]
    }
  },
  computed: {
    filteredWarnings () {
      if (!this.warningSearch) return this.warningsHistory
      return this.warningsHistory.filter(w =>
        w.player.toLowerCase().includes(this.warningSearch.toLowerCase())
      )
    },
    playersWithWarnings () {
      const players = {}
      this.warningsHistory.forEach(warning => {
        if (!players[warning.player_id]) {
          players[warning.player_id] = {
            player_id: warning.player_id,
            player_name: warning.player,
            initials: this.getInitials(warning.player),
            total_warnings: 0,
            strikes: 0,
            last_warning: null,
            restricted: false,
            warnings: []
          }
        }
        players[warning.player_id].total_warnings += 1
        players[warning.player_id].strikes += warning.strikes
        players[warning.player_id].last_warning = warning.date
        players[warning.player_id].warnings.push(warning)

        // Determinar si está bloqueado (3+ strikes)
        if (players[warning.player_id].strikes >= 3) {
          players[warning.player_id].restricted = true
        }
      })
      return Object.values(players)
    },
    filteredPlayers () {
      if (!this.playerSearch) return this.playersWithWarnings
      return this.playersWithWarnings.filter(p =>
        p.player_name.toLowerCase().includes(this.playerSearch.toLowerCase())
      )
    }
  },
  methods: {
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getInitials (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    },
    getCategoryColor (category) {
      const colors = {
        pace: 'orange',
        damage: 'red',
        attire: 'blue',
        conduct: 'red',
        rules: 'purple',
        warning: 'grey'
      }
      return colors[category] || 'grey'
    },
    getCategoryLabel (category) {
      const labels = {
        pace: 'Ritmo',
        damage: 'Daño',
        attire: 'Vestimenta',
        conduct: 'Conducta',
        rules: 'Reglas',
        warning: 'Aviso'
      }
      return labels[category] || 'Otro'
    },
    getSeverityColor (severity) {
      const colors = {
        0: 'grey',
        1: 'orange',
        2: 'red'
      }
      return colors[severity] || 'grey'
    },
    getStrikesColor (strikes) {
      if (strikes >= 3) return 'red'
      if (strikes >= 2) return 'orange'
      return 'green'
    },
    viewWarningDetails (warning) {
      this.selectedWarning = { ...warning }
      this.warningDetailDialog = true
    },
    viewPlayerFile (player) {
      this.selectedPlayer = player
      this.selectedPlayerWarnings = player.warnings
      this.playerFileDialog = true
    }
  }
}
</script>

<style scoped>
</style>
